import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { seoArticles } from "@/mocks/seoArticles";
import type { ArticleSection } from "@/mocks/seoArticles";

function renderSection(section: ArticleSection, index: number) {
  switch (section.type) {
    case "heading":
      return (
        <h2 key={index} className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4 mt-10">
          {section.heading}
        </h2>
      );

    case "paragraph":
      return (
        <div key={index} className="space-y-4 mb-6">
          {section.paragraphs?.map((p, pi) => (
            <p key={pi} className="text-foreground-700 text-base leading-relaxed">{p}</p>
          ))}
        </div>
      );

    case "list":
      return (
        <div key={index} className="space-y-3 mb-6">
          {section.items?.map((item, ii) => (
            <div key={ii} className="flex items-start gap-3 bg-background-100 rounded-xl p-4 border border-background-200/70">
              <span className="w-7 h-7 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-check-line text-accent-600 text-sm"></i>
              </span>
              <div>
                <strong className="text-foreground-900 text-sm">{item.title}</strong>
                <p className="text-foreground-600 text-sm mt-1">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      );

    case "steps":
      return (
        <div key={index} className="space-y-4 mb-6">
          {section.steps?.map((step, si) => (
            <div key={si} className="flex items-start gap-4 bg-background-100 rounded-xl p-5 border border-background-200/70">
              <span className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0 text-background-50 dark:text-foreground-950 font-bold text-sm">
                {si + 1}
              </span>
              <div>
                <strong className="text-foreground-900 text-sm">{step.title}</strong>
                <p className="text-foreground-600 text-sm mt-1">{step.text}</p>
              </div>
            </div>
          ))}
        </div>
      );

    case "cta":
      return (
        <div key={index} className="mt-10 p-6 bg-primary-50 rounded-2xl border border-primary-200 text-center">
          <h3 className="font-heading text-xl font-bold text-foreground-950 mb-3">
            {section.heading}
          </h3>
          {section.paragraphs?.map((p, pi) => (
            <p key={pi} className="text-foreground-600 text-sm mb-5 max-w-lg mx-auto">{p}</p>
          ))}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 font-semibold text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-database-2-line text-base"></i>
              AllChinaBuy Sheet
            </a>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-background-50 border border-background-300/60 text-foreground-700 font-medium text-sm rounded-full hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-4-line text-base"></i>
              Back to Homepage
            </Link>
          </div>
        </div>
      );

    default:
      return null;
  }
}

function NotFoundArticle() {
  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center">
      <div className="text-center px-6">
        <div className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-6">
          <i className="ri-file-search-line text-foreground-400 text-3xl"></i>
        </div>
        <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">Article Not Found</h1>
        <p className="text-foreground-500 text-sm mb-6">The article you are looking for does not exist or has been moved.</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 font-medium text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-home-4-line text-base"></i>
          Back to Homepage
        </Link>
      </div>
    </div>
  );
}

export default function SEOArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = seoArticles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (!article) return;
    const siteUrl = import.meta.env.VITE_SITE_URL || "https://allchinabuy.team";
    const articleData = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.title,
      "description": article.description,
      "url": `${siteUrl}/article/${article.slug}`,
      "datePublished": article.date,
      "dateModified": "2026-06-25",
      "author": {
        "@type": "Organization",
        "name": "AllChinaBuy Spreadsheet",
        "url": siteUrl
      },
      "publisher": {
        "@type": "Organization",
        "name": "AllChinaBuy Spreadsheet",
        "url": siteUrl,
        "logo": {
          "@type": "ImageObject",
          "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/14bc629c50c04cb08f38590896596985.jpeg"
        }
      }
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(articleData);
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [slug, article]);

  if (!article) {
    return <NotFoundArticle />;
  }

  return (
    <div className="min-h-screen bg-background-50">
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-file-list-3-line text-background-50 text-base"></i>
              </div>
              <span className="font-heading font-semibold text-foreground-950 text-base whitespace-nowrap">
                AllChinaBuy Sheet
              </span>
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line text-base"></i>
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      <article className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <header className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors">
              Home
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400 text-sm"></i>
            <span className="text-sm text-foreground-500">{article.title.length > 50 ? article.title.substring(0, 50) + "..." : article.title}</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
            {article.title}
          </h1>
          <p className="text-foreground-600 text-base leading-relaxed mb-4">
            {article.description}
          </p>
          <div className="flex items-center gap-4 text-sm text-foreground-500">
            <span>Published: {article.date}</span>
            <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
            <span>{article.readTime}</span>
          </div>
        </header>

        <div className="relative rounded-2xl overflow-hidden mb-12">
          <img
            src={article.image}
            alt={article.title}
            title={article.title}
            className="w-full h-auto object-cover"
          />
        </div>

        <div>
          {article.sections.map((section, index) => renderSection(section, index))}
        </div>

        <div className="mt-12 p-6 bg-background-100 rounded-2xl border border-background-200/70 text-center">
          <h3 className="font-heading text-lg font-bold text-foreground-950 mb-3">
            Want More China Sourcing Insights?
          </h3>
          <p className="text-foreground-600 text-sm mb-4 max-w-md mx-auto">
            Explore our complete guide or browse other SEO articles for more China sourcing strategies and tips.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/allchinabuy-spreadsheet-guide"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 font-semibold text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-book-open-line text-base"></i>
              Complete Guide
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-background-50 border border-background-300/60 text-foreground-700 font-medium text-sm rounded-full hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-4-line text-base"></i>
              Back to Homepage
            </Link>
          </div>
        </div>
      </article>

      <footer className="bg-secondary-100 py-8 border-t border-background-200/70">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-foreground-950 transition-colors mb-3">
            <i className="ri-arrow-left-line text-base"></i>
            Return to AllChinaBuy Spreadsheet Home
          </Link>
          <p className="text-foreground-500 text-sm">
            &copy; {new Date().getFullYear()} AllChinaBuy Spreadsheet. Your trusted China sourcing intelligence platform.
          </p>
        </div>
      </footer>
    </div>
  );
}